import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Toaster, toast } from "react-hot-toast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowUpRightFromSquare,
  faCompress,
  faExpand,
  faPlus,
  faQrcode,
  faTrash,
  faXmark,
  faCheck,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import { motion, AnimatePresence } from "framer-motion";
import { BASE_URL } from "../../config";

const allowedFileTypes = ["pdf", "jpg", "jpeg", "png"];
const maxFileSizeMB = 10;
const maxRtpUploadLimit = 2;

const normalizeLoaScanInput = (value) => {
  const rawValue = String(value ?? "");
  let candidate = rawValue.trim();

  for (let attempt = 0; attempt < 4; attempt += 1) {
    try {
      const payload = JSON.parse(candidate);
      if (typeof payload === "string") {
        candidate = payload.trim();
        continue;
      }
      const loaNo = Object.entries(payload ?? {}).find(
        ([key]) => key.toUpperCase() === "LOA_NO"
      )?.[1];
      if (typeof loaNo === "string" || typeof loaNo === "number") {
        return String(loaNo).trim().toUpperCase();
      }
    } catch (error) {
      // A scanner may wrap the JSON in quotes or send escaped JSON text.
    }

    const jsonStart = candidate.indexOf("{");
    const jsonEnd = candidate.lastIndexOf("}");
    if (jsonStart === -1 || jsonEnd <= jsonStart) break;

    const jsonPayload = candidate.slice(jsonStart, jsonEnd + 1);
    const nextCandidate =
      jsonPayload !== candidate
        ? jsonPayload
        : candidate.replace(/\\"/g, '"');
    if (nextCandidate === candidate) break;
    candidate = nextCandidate;
  }

  // The LOA can still be read when the scanner damages the remaining metadata.
  const loaMatch = candidate.match(/"LOA_NO"\s*:\s*"([^"]+)"/i);
  if (loaMatch) return loaMatch[1].trim().toUpperCase();

  if (rawValue.includes("{")) return rawValue;
  return rawValue.replace(/^[\s"']+|[\s"']+$/g, "").toUpperCase();
};

const getStoredUserName = () => {
  try {
    const raw = localStorage.getItem("authentication/user");
    if (raw) {
      const parsed = JSON.parse(raw);
      const full =
        parsed.full_name ||
        [parsed.first_name, parsed.last_name].filter(Boolean).join(" ");
      if (full) return full;
    }
  } catch (err) {
    // ignore parse errors
  }
  const first = localStorage.getItem("user_first_name");
  const last = localStorage.getItem("user_last_name");
  const fallback = [first, last].filter(Boolean).join(" ");
  return fallback || localStorage.getItem("user_name") || "";
};

const toInputDate = (date) => date.toLocaleDateString("en-CA");
const getTodayInputDate = () => toInputDate(new Date());
const isFutureInputDate = (value) =>
  Boolean(value) && value > getTodayInputDate();

const getDateReceivedValidationMessage = (value) => {
  if (!value) return "Please select Date Received first.";
  if (isFutureInputDate(value)) {
    return "Date Received cannot be a future date.";
  }
  return "";
};

const addBusinessDays = (dateString, days) => {
  if (!dateString) return "";
  const parsedDays = parseInt(String(days).match(/\d+/)?.[0] ?? "", 10);
  if (Number.isNaN(parsedDays)) return "";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "";
  let added = 0;
  while (added < parsedDays) {
    date.setDate(date.getDate() + 1);
    const day = date.getDay();
    if (day !== 0 && day !== 6) {
      added += 1;
    }
  }
  return toInputDate(date);
};

const formatDateForCopy = (value) => {
  if (!value) return "";
  const parts = String(value).split("-");
  if (parts.length === 3 && parts[0].length === 4) {
    const [year, month, day] = parts;
    return `${month.padStart(2, "0")}-${day.padStart(2, "0")}-${year}`;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const year = date.getFullYear();
  return `${month}-${day}-${year}`;
};

const copyToClipboard = async (text) => {
  if (!text) return false;
  if (navigator?.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return true;
  }
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  const success = document.execCommand("copy");
  document.body.removeChild(textarea);
  return success;
};

const DEFAULT_ALLOWED_EXTRACTED_COVERAGES = [
  "OUTPATIENT-OPD-OR",
  "INPATIENT-ADMISSION",
];
const DEFAULT_EXCLUDED_BATCH_RULES = [
  { coverage_code: "INPATIENT-ADMISSION", claim_status: "EXTRACTED" },
  { coverage_code: "OUTPATIENT-OPD-OR", claim_status: "EXTRACTED" },
];

const normalizeCoverageList = (values) => {
  if (!Array.isArray(values)) return [];
  const normalized = values
    .map((value) => String(value || "").trim().toUpperCase())
    .filter((value) => value !== "");
  return Array.from(new Set(normalized));
};

const normalizeExcludedRules = (values) => {
  if (!Array.isArray(values)) return [];
  const normalized = values
    .map((rule) => {
      if (!rule || typeof rule !== "object") return null;
      const coverage = String(
        rule.coverage_code || rule.coverage || ""
      ).trim().toUpperCase();
      const status = String(
        rule.claim_status || rule.status || ""
      ).trim().toUpperCase();
      if (!coverage || !status) return null;
      return { coverage_code: coverage, claim_status: status };
    })
    .filter(Boolean);
  const unique = new Map();
  normalized.forEach((rule) => {
    unique.set(`${rule.coverage_code}:${rule.claim_status}`, rule);
  });
  return Array.from(unique.values());
};

const ScanLoaDocumentQrModal = ({
  isOpen,
  onClose,
  onProceed,
  initialData = null,
  variant = "modal",
}) => {
  const isFullPage = variant === "fullpage";
  const [loaInput, setLoaInput] = useState("");
  const [providerName, setProviderName] = useState("");
  const [providerCode, setProviderCode] = useState("");
  const [paymentTerms, setPaymentTerms] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [soaRef, setSoaRef] = useState("");
  const [soaAmount, setSoaAmount] = useState("");
  const [actualClaimCount, setActualClaimCount] = useState("");
  const [dateReceived, setDateReceived] = useState("");
  const [receivedBy, setReceivedBy] = useState("");
  const [remarks, setRemarks] = useState("");
  const [loaRows, setLoaRows] = useState([]);
  const [selectedLoas, setSelectedLoas] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const submitLockRef = useRef(false);
  const pendingLoaRef = useRef(new Set());
  const [isExpanded, setIsExpanded] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [allowedExtractedCoverages, setAllowedExtractedCoverages] = useState(
    DEFAULT_ALLOWED_EXTRACTED_COVERAGES
  );
  const [excludedBatchRules, setExcludedBatchRules] = useState(
    DEFAULT_EXCLUDED_BATCH_RULES
  );
  const [rtpReasons, setRtpReasons] = useState([]);
  const [isRtpModalOpen, setIsRtpModalOpen] = useState(false);
  const [rtpModalRow, setRtpModalRow] = useState(null);
  const [rtpModalReason, setRtpModalReason] = useState("");
  const [rtpModalRemarks, setRtpModalRemarks] = useState("");
  const [rtpAttachments, setRtpAttachments] = useState([]);
  const [rtpUploadQueue, setRtpUploadQueue] = useState([]);
  const pageSize = 5;
  const todayInputDate = useMemo(() => getTodayInputDate(), []);
  const phpFormatter = useMemo(
    () =>
      new Intl.NumberFormat("en-PH", {
        style: "currency",
        currency: "PHP",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    []
  );

  const formatAmount = (value) => {
    if (value === null || value === undefined || value === "") return "-";
    const numeric = Number(value);
    if (Number.isNaN(numeric)) return value;
    return phpFormatter.format(numeric);
  };

  const resolveApiErrorMessage = (error, fallback) => {
    const message = error.response?.data?.message;
    if (!message) return fallback;
    if (typeof message === "string") return message;
    if (Array.isArray(message)) return message.join(", ");
    if (typeof message === "object") return Object.values(message).flat().join(", ");
    return fallback;
  };

  const handleCopyDateReceived = async () => {
    if (!dateReceived) {
      toast.error("Please select Date Received first.");
      return;
    }
    const formatted = formatDateForCopy(dateReceived);
    if (!formatted) {
      toast.error("Invalid Date Received value.");
      return;
    }
    try {
      const copied = await copyToClipboard(formatted);
      if (copied) {
        toast.success(`Date Received copied: ${formatted}`);
      } else {
        toast.error("Unable to copy Date Received. Please try again.");
      }
    } catch (error) {
      toast.error("Unable to copy Date Received. Please try again.");
    }
  };

  const hasRows = loaRows.length > 0;
  const allSelected = hasRows && selectedLoas.length === loaRows.length;

  const resetForm = () => {
    setLoaInput("");
    setProviderName("");
    setProviderCode("");
    setPaymentTerms("");
    setDueDate("");
    setSoaRef("");
    setSoaAmount("");
    setActualClaimCount("");
    setDateReceived("");
    setReceivedBy(getStoredUserName());
    setRemarks("");
    setLoaRows([]);
    setSelectedLoas([]);
    setIsRtpModalOpen(false);
    setRtpModalRow(null);
    setRtpModalReason("");
    setRtpModalRemarks("");
    setRtpAttachments([]);
    setRtpUploadQueue([]);
  };

  const hydrateForm = useCallback((data) => {
    const nextData = data || {};
    setLoaInput(normalizeLoaScanInput(nextData.loaInput || ""));
    setProviderName(nextData.providerName || "");
    setProviderCode(nextData.providerCode || "");
    setPaymentTerms(nextData.paymentTerms || "");
    setDueDate(nextData.dueDate || "");
    setSoaRef(nextData.soaRef || "");
    setSoaAmount(nextData.soaAmount ?? "");
    setActualClaimCount(nextData.actualClaimCount ?? "");
    setDateReceived(nextData.dateReceived || "");
    setReceivedBy(nextData.receivedBy || getStoredUserName());
    setRemarks(nextData.remarks || "");
    setLoaRows(Array.isArray(nextData.loaRows) ? nextData.loaRows : []);
    setSelectedLoas([]);
    setSearchTerm("");
    setPage(1);
    setAllowedExtractedCoverages(
      Array.isArray(nextData.allowedExtractedCoverages)
        ? normalizeCoverageList(nextData.allowedExtractedCoverages)
        : DEFAULT_ALLOWED_EXTRACTED_COVERAGES
    );
    setExcludedBatchRules(
      Array.isArray(nextData.excludedBatchRules)
        ? normalizeExcludedRules(nextData.excludedBatchRules)
        : DEFAULT_EXCLUDED_BATCH_RULES
    );
    setIsRtpModalOpen(false);
    setRtpModalRow(null);
    setRtpModalReason("");
    setRtpModalRemarks("");
    setRtpAttachments([]);
    setRtpUploadQueue([]);
  }, []);

  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        hydrateForm(initialData);
      } else {
        setReceivedBy(getStoredUserName());
      }
    } else {
      resetForm();
    }
  }, [isOpen, initialData, hydrateForm]);

  useEffect(() => {
    if (!isOpen || !loaInput) return;
    if (loaInput.includes("{") && !/}\s*["']?\s*$/.test(loaInput)) return;

    // Keyboard scanners type in bursts. Keep the raw text and caret unchanged
    // until the complete scan has stopped arriving, or Enter/blur completes it.
    const timeout = window.setTimeout(() => {
      setLoaInput((current) =>
        current === loaInput ? normalizeLoaScanInput(current) : current
      );
    }, 500);

    return () => window.clearTimeout(timeout);
  }, [isOpen, loaInput]);

  useEffect(() => {
    if (!isOpen) return;
    let isActive = true;

    const fetchAllowedCoverages = async () => {
      try {
        const token = localStorage.getItem("authentication/token");
        if (!token) return;
        const response = await axios.get(
          `${BASE_URL}/claims/receiving/allowed-coverages`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        const raw = response.data?.data?.allowed_coverages;
        const excludedRaw = response.data?.data?.excluded_rules;
        if (Array.isArray(raw) && isActive) {
          setAllowedExtractedCoverages(normalizeCoverageList(raw));
        }
        if (Array.isArray(excludedRaw) && isActive) {
          const normalizedExcluded = normalizeExcludedRules(excludedRaw);
          setExcludedBatchRules(
            normalizedExcluded.length
              ? normalizedExcluded
              : DEFAULT_EXCLUDED_BATCH_RULES
          );
        }
      } catch (error) {
        // keep defaults on error
      }
    };

    fetchAllowedCoverages();
    return () => {
      isActive = false;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    let isActive = true;
    const fetchRtpReasons = async () => {
      try {
        const token = localStorage.getItem("authentication/token");
        if (!token) return;
        const response = await axios.get(`${BASE_URL}/claims/processing/rtp-reasons`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (isActive) {
          setRtpReasons(response.data?.data || []);
        }
      } catch (error) {
        if (isActive) {
          setRtpReasons([]);
        }
      }
    };

    fetchRtpReasons();
    return () => {
      isActive = false;
    };
  }, [isOpen]);

  const handleDateReceivedChange = (value) => {
    if (isFutureInputDate(value)) {
      setDateReceived("");
      setDueDate("");
      toast.error("Date Received cannot be a future date.");
      return;
    }

    setDateReceived(value);
    if (paymentTerms) {
      const nextDueDate = addBusinessDays(value, paymentTerms);
      setDueDate(nextDueDate);
    } else {
      setDueDate(value);
    }
  };

  const handleRemoveRow = (loaNo) => {
    const nextRows = loaRows.filter((row) => row.loaNo !== loaNo);
    setLoaRows(nextRows);
    setSelectedLoas((prev) => prev.filter((item) => item !== loaNo));
    if (nextRows.length === 0) {
      setProviderName("");
      setProviderCode("");
      setPaymentTerms("");
      setDueDate("");
    }
  };

  const handleToggleRow = (loaNo) => {
    setSelectedLoas((prev) =>
      prev.includes(loaNo)
        ? prev.filter((item) => item !== loaNo)
        : [...prev, loaNo]
    );
  };

  const handleToggleAll = () => {
    if (allSelected) {
      setSelectedLoas([]);
    } else {
      setSelectedLoas(loaRows.map((row) => row.loaNo));
    }
  };

  const handleRemoveSelected = () => {
    if (selectedLoas.length === 0) return;
    const confirmed = window.confirm(
      "Are you sure you want to remove the selected LOA entries?"
    );
    if (!confirmed) return;
    const nextRows = loaRows.filter((row) => !selectedLoas.includes(row.loaNo));
    setLoaRows(nextRows);
    setSelectedLoas([]);
    if (nextRows.length === 0) {
      setProviderName("");
      setProviderCode("");
      setPaymentTerms("");
      setDueDate("");
    }
  };

  const handleScanSubmit = async (input = loaInput) => {
    const trimmed = normalizeLoaScanInput(input).trim();
    if (!trimmed) {
      window.alert("Please enter a LOA# first.");
      return;
    }
    if (trimmed.includes("{")) {
      window.alert(
        "Unable to read LOA# from the QR code. Please scan again or enter the LOA#."
      );
      return;
    }
    setLoaInput(trimmed);
    if (pendingLoaRef.current.has(trimmed)) {
      return;
    }
    if (loaRows.some((row) => row.loaNo === trimmed)) {
      window.alert("LOA already added.");
      setLoaInput("");
      return;
    }
    const dateReceivedMessage = getDateReceivedValidationMessage(dateReceived);
    if (dateReceivedMessage) {
      window.alert(dateReceivedMessage);
      return;
    }

    if (isFetching || submitLockRef.current) {
      return;
    }
    submitLockRef.current = true;
    pendingLoaRef.current.add(trimmed);
    setIsFetching(true);
    try {
      const token = localStorage.getItem("authentication/token");
      const response = await axios.get(
        `${BASE_URL}/claims/scan-loa/${encodeURIComponent(trimmed)}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const payload = response.data?.data ?? response.data ?? [];
      if (!payload || payload.length === 0) {
        window.alert(`LOA ${trimmed} not found from the system.`);
        setLoaInput("");
        return;
      }

      const details = payload[0];
      const nextProviderCode = details.provider_code || "";

      if (providerCode && providerCode !== nextProviderCode) {
        window.alert(
          `Different Provider. LOA ${details.loa_no || trimmed} belongs to ${details.provider_name}.`
        );
        setLoaInput("");
        return;
      }

      setProviderCode(nextProviderCode);
      setProviderName(details.provider_name || "");
      setPaymentTerms(details.payment_terms || "");
      if (details.payment_terms) {
        setDueDate(addBusinessDays(dateReceived, details.payment_terms));
      } else if (dateReceived) {
        setDueDate(dateReceived);
      }

      const staleValue =
        details.stale_bol ?? details.stale ?? details.stale_tag ?? false;
      const isStale =
        staleValue === true ||
        staleValue === 1 ||
        staleValue === "1" ||
        String(staleValue).toLowerCase() === "true";

      setLoaRows((prev) => {
        const loaNo = details.loa_no || details.trans_no || trimmed;
        if (prev.some((row) => row.loaNo === loaNo)) {
          return prev;
        }
        return [
          ...prev,
          {
            loaNo,
            transNo: details.trans_no || "",
            coverageCode: details.coverage_code || "-",
            dateReceived,
            receivedBy: receivedBy || "-",
            loaAmount: details.loa_amount ?? null,
            claimAmount: details.claim_amount ?? null,
            claimStatus: details.claim_status || "",
            stale: isStale,
            rtpTag: isStale,
            rtpCode: "",
            rtpRemarks: "",
            rtpAttachments: [],
          },
        ];
      });
      setLoaInput("");
    } catch (err) {
      window.alert("Unable to fetch LOA details. Please try again.");
    } finally {
      setIsFetching(false);
      submitLockRef.current = false;
      pendingLoaRef.current.delete(trimmed);
    }
  };

  const handleOpenRtpModal = (row) => {
    if (!row?.transNo) {
      toast.error("Missing transaction number for RTP.");
      return;
    }
    setRtpModalRow(row);
    setRtpModalReason(row.rtpCode || "");
    setRtpModalRemarks(row.rtpRemarks || "");
    setRtpAttachments(row.rtpAttachments || []);
    setRtpUploadQueue([]);
    setIsRtpModalOpen(true);
  };

  const handleCloseRtpModal = () => {
    setIsRtpModalOpen(false);
    setRtpModalRow(null);
    setRtpModalReason("");
    setRtpModalRemarks("");
    setRtpAttachments([]);
    setRtpUploadQueue([]);
  };

  const handleConfirmRtp = () => {
    if (!rtpModalRow) return;
    setLoaRows((prev) =>
      prev.map((row) =>
        row.loaNo === rtpModalRow.loaNo
          ? {
              ...row,
              rtpTag: true,
              rtpCode: rtpModalReason,
              rtpRemarks: rtpModalRemarks,
              rtpAttachments,
            }
          : row
      )
    );
    handleCloseRtpModal();
  };

  const handleCancelRtpTag = (loaNo) => {
    setLoaRows((prev) =>
      prev.map((row) =>
        row.loaNo === loaNo
          ? {
              ...row,
              rtpTag: false,
              rtpCode: "",
              rtpRemarks: "",
            }
          : row
      )
    );
  };

  const handleRtpFileChange = (selectedFiles) => {
    const transNo = rtpModalRow?.transNo;
    if (!transNo) {
      toast.error("Missing transaction number for RTP attachments.");
      return;
    }
    const validFiles = Array.from(selectedFiles).filter((file) => {
      const fileExtension = file.name.split(".").pop().toLowerCase();
      const isAllowedType = allowedFileTypes.includes(fileExtension);
      const isWithinSizeLimit = file.size <= maxFileSizeMB * 1024 * 1024;

      if (!isAllowedType) {
        toast.error(`File ${file.name} is not supported.`, { duration: 4000 });
        return false;
      }

      if (!isWithinSizeLimit) {
        toast.error(`File ${file.name} exceeds ${maxFileSizeMB}MB size limit.`, {
          duration: 4000,
        });
        return false;
      }

      return true;
    });

    const existingCount = rtpAttachments.length + rtpUploadQueue.length;
    if (existingCount + validFiles.length > maxRtpUploadLimit) {
      toast.error(`You can only upload up to ${maxRtpUploadLimit} RTP attachment files.`, {
        duration: 4000,
      });
      return;
    }

    const newFiles = validFiles.map((file) => ({
      file,
      progress: 0,
      isUploading: false,
      error: null,
    }));

    setRtpUploadQueue((prev) => [...prev, ...newFiles]);
    newFiles.forEach((fileObj) => handleUploadRtpAttachment(fileObj, transNo));
  };

  const handleUploadRtpAttachment = async (fileObj, transNo) => {
    if (!transNo) return;
    setRtpUploadQueue((prev) =>
      prev.map((item) => (item.file === fileObj.file ? { ...item, isUploading: true } : item))
    );

    try {
      const formData = new FormData();
      formData.append("file", fileObj.file);
      formData.append("trans_no", transNo);

      const response = await axios.post(
        `${BASE_URL}/claims/processing/rtp/attach-files`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authentication/token")}`,
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setRtpUploadQueue((prev) =>
              prev.map((item) =>
                item.file === fileObj.file ? { ...item, progress } : item
              )
            );
          },
        }
      );

      const uploadedFile = response.data?.files?.[0];
      if (uploadedFile) {
        const attachment = {
          upload_id: uploadedFile.upload_id,
          original_file_name: uploadedFile.original_file_name,
          file_size: fileObj.file.size,
          signed_url: uploadedFile.signed_url,
          url_expires_at: uploadedFile.expires_at,
        };
        setRtpAttachments((prev) => [attachment, ...prev]);
        setLoaRows((prev) =>
          prev.map((row) =>
            row.loaNo === rtpModalRow?.loaNo
              ? {
                  ...row,
                  rtpAttachments: [attachment, ...(row.rtpAttachments || [])],
                }
              : row
          )
        );
      }

      setRtpUploadQueue((prev) => prev.filter((item) => item.file !== fileObj.file));
      toast.success(`File ${fileObj.file.name} uploaded successfully.`, { duration: 4000 });
    } catch (error) {
      const errorMessage = resolveApiErrorMessage(error, "Failed to upload file.");
      setRtpUploadQueue((prev) =>
        prev.map((item) =>
          item.file === fileObj.file ? { ...item, isUploading: false, error: errorMessage } : item
        )
      );
      toast.error(errorMessage, { duration: 4000 });
    }
  };

  const handleRemoveRtpAttachment = async (fileObj) => {
    if (!fileObj?.upload_id) return;
    const confirmed = window.confirm("Remove this attachment?");
    if (!confirmed) return;

    try {
      await axios.delete(`${BASE_URL}/claims/processing/rtp/attachments/${fileObj.upload_id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authentication/token")}`,
        },
      });
      setRtpAttachments((prev) => prev.filter((item) => item.upload_id !== fileObj.upload_id));
      setLoaRows((prev) =>
        prev.map((row) =>
          row.loaNo === rtpModalRow?.loaNo
            ? {
                ...row,
                rtpAttachments: (row.rtpAttachments || []).filter(
                  (item) => item.upload_id !== fileObj.upload_id
                ),
              }
            : row
        )
      );
      toast.success("Attachment removed.", { duration: 3000 });
    } catch (error) {
      toast.error(resolveApiErrorMessage(error, "Failed to remove attachment."), {
        duration: 4000,
      });
    }
  };

  const handleProceed = () => {
    if (
      !soaRef.trim() ||
      !soaAmount.toString().trim() ||
      !actualClaimCount.toString().trim() ||
      !dateReceived ||
      loaRows.length === 0
    ) {
      window.alert("Please complete required fields and add at least one LOA.");
      return;
    }

    const dateReceivedMessage = getDateReceivedValidationMessage(dateReceived);
    if (dateReceivedMessage) {
      window.alert(dateReceivedMessage);
      return;
    }

    const parsedSoaAmount = Number(soaAmount);
    if (Number.isNaN(parsedSoaAmount) || parsedSoaAmount < 0) {
      window.alert("Please enter a valid SOA Amount.");
      return;
    }

    const parsedClaimCount = Number(actualClaimCount);
    if (
      Number.isNaN(parsedClaimCount) ||
      !Number.isInteger(parsedClaimCount) ||
      parsedClaimCount < 0
    ) {
      window.alert("Please enter a valid Actual Claim Count.");
      return;
    }

    if (parsedClaimCount !== loaRows.length) {
      window.alert(
        "Claim Count does not match the attached LOA count. Please review the batch before continuing."
      );
      return;
    }

    onProceed({
      providerCode,
      providerName,
      paymentTerms,
      dateReceived,
      dueDate,
      receivedBy,
      soaRef,
      soaAmount: parsedSoaAmount,
      actualClaimCount: parsedClaimCount,
      remarks,
      loaRows,
      allowedExtractedCoverages,
      excludedBatchRules,
    });
  };

  const selectCountLabel = useMemo(() => {
    if (!hasRows) return "0 selected";
    return `${selectedLoas.length} selected`;
  }, [selectedLoas, hasRows]);

  const filteredRows = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return loaRows;
    return loaRows.filter((row) => {
      const loaNo = row.loaNo?.toString().toLowerCase() ?? "";
      const coverage = row.coverageCode?.toString().toLowerCase() ?? "";
      const date = row.dateReceived?.toString().toLowerCase() ?? "";
      return (
        loaNo.includes(term) || coverage.includes(term) || date.includes(term)
      );
    });
  }, [loaRows, searchTerm]);

  const isAllowedExtractedCoverage = (row) => {
    const status = (row.claimStatus || "").trim().toLowerCase();
    if (status !== "extracted") return false;
    const coverage = (row.coverageCode || "").trim().toUpperCase();
    return allowedExtractedCoverages.includes(coverage);
  };

  /*
  // OLD SPEC (receive-only excluded):
  const isReceiveOnlyRow = (row) => {
    const status = (row.claimStatus || "").trim().toUpperCase();
    if (!status) return false;
    const coverage = (row.coverageCode || "").trim().toUpperCase();
    return excludedBatchRules.some(
      (rule) =>
        rule.coverage_code === coverage && rule.claim_status === status
    );
  };

  const isBlockedRow = (row) => {
    const status = (row.claimStatus || "").trim();
    if (!status) return false;
    if (isReceiveOnlyRow(row)) return false;
    return !isAllowedExtractedCoverage(row);
  };
  */

  // NEW SPEC: no receive-only exclusion; extracted + allowed coverages can be batched.
  const isBlockedRow = (row) => {
    const status = (row.claimStatus || "").trim();
    if (!status) return false;
    return !isAllowedExtractedCoverage(row);
  };

  const blockedRows = useMemo(
    () => loaRows.filter(isBlockedRow),
    [loaRows, allowedExtractedCoverages, excludedBatchRules]
  );
  /*
  // OLD SPEC (receive-only + RTP excluded):
  const receiveOnlyRows = useMemo(
    () => loaRows.filter(isReceiveOnlyRow),
    [loaRows, excludedBatchRules]
  );
  const rtpRows = useMemo(() => loaRows.filter((row) => row.rtpTag), [loaRows]);
  */
  const allowedCoverageLabel = useMemo(() => {
    if (allowedExtractedCoverages.length === 0) return "None";
    return allowedExtractedCoverages.join(", ");
  }, [allowedExtractedCoverages]);

  const totalLoaAmount = useMemo(
    () =>
      loaRows.reduce(
        (sum, row) => sum + (Number(row.loaAmount) || 0),
        0
      ),
    [loaRows]
  );
  const totalClaimAmount = useMemo(
    () =>
      loaRows.reduce(
        (sum, row) => sum + (Number(row.claimAmount) || 0),
        0
      ),
    [loaRows]
  );

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize));
  const pagedRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [filteredRows, page, pageSize]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(1);
    }
  }, [page, totalPages]);

  const isDirty = useMemo(
    () =>
      Boolean(
        loaInput.trim() ||
          providerName.trim() ||
          paymentTerms.toString().trim() ||
          dueDate.trim() ||
          soaRef.trim() ||
          soaAmount.toString().trim() ||
          actualClaimCount.toString().trim() ||
          dateReceived.trim() ||
          remarks.trim() ||
          loaRows.length > 0
      ),
    [
      loaInput,
      providerName,
      paymentTerms,
      dueDate,
      soaRef,
      soaAmount,
      actualClaimCount,
      dateReceived,
      remarks,
      loaRows.length,
    ]
  );

  const handleClose = () => {
    if (!isDirty) {
      onClose();
      return;
    }
    const confirmed = window.confirm(
      "Closing this modal will discard your changes. Continue?"
    );
    if (confirmed) {
      onClose();
    }
  };

  const buildFullPageSnapshot = () => ({
    loaInput,
    providerName,
    providerCode,
    paymentTerms,
    dueDate,
    soaRef,
    soaAmount,
    actualClaimCount,
    dateReceived,
    receivedBy,
    remarks,
    loaRows,
    allowedExtractedCoverages,
    excludedBatchRules,
  });

  const handleOpenFullPage = () => {
    const stateKey = `scan-loa-document-qr-${Date.now()}`;
    try {
      sessionStorage.setItem(stateKey, JSON.stringify(buildFullPageSnapshot()));
    } catch (error) {
      toast.error("Unable to prepare full page view. Please try again.");
      return;
    }
    window.open(
      `/claims/receiving/scan-loa-document-qr?state_key=${encodeURIComponent(stateKey)}`,
      "_blank"
    );
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <Toaster position="top-right" toastOptions={{ duration: 3000 }} />
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className={
          isFullPage
            ? "min-h-screen bg-slate-100"
            : "fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 py-6 backdrop-blur-sm"
        }
      >
        <motion.div
          initial={{ scale: 0.96, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.96, opacity: 0 }}
          className={
            isFullPage
              ? "min-h-screen w-full bg-white p-6 overflow-y-auto"
              : `w-full rounded-2xl border border-slate-100 bg-white p-6 shadow-2xl overflow-y-auto ${
                  isExpanded ? "max-w-6xl max-h-[95vh]" : "max-w-5xl max-h-[92vh]"
                }`
          }
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-500">
                Scan LOA Document QR
              </p>
              <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
                <FontAwesomeIcon icon={faQrcode} className="text-indigo-600" />
                Scan LOA Document QR
              </h2>
            </div>
            <div className="flex items-center gap-2">
              {!isFullPage && (
                <button
                  type="button"
                  onClick={handleOpenFullPage}
                  className="relative inline-flex items-center gap-1.5 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-xs font-semibold text-indigo-700 transition-colors hover:bg-indigo-100"
                  title="Open in a new full page tab"
                >
                  <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                  View in Full Page
                  <span className="absolute -right-2 -top-2 inline-flex items-center rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-bold leading-none text-white shadow-sm">
                    New
                  </span>
                </button>
              )}
              {!isFullPage && (
                <button
                  onClick={() => setIsExpanded((prev) => !prev)}
                  className="text-slate-400 hover:text-slate-600 transition"
                  aria-label={isExpanded ? "Restore size" : "Maximize"}
                >
                  <FontAwesomeIcon icon={isExpanded ? faCompress : faExpand} />
                </button>
              )}
              <button
                onClick={handleClose}
                className="text-slate-400 hover:text-slate-600 transition"
                aria-label="Close"
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap justify-end gap-3">
            <button
              type="button"
              onClick={handleClose}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50"
            >
              <FontAwesomeIcon icon={faXmark} />
              Close
            </button>
            <button
              type="button"
              onClick={handleProceed}
              disabled={blockedRows.length > 0}
              className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
            >
              <FontAwesomeIcon icon={faCheck} />
              Proceed Submit to Create Batch
            </button>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-12">
            <div className="md:col-span-4">
              <label className="text-xs font-semibold uppercase text-slate-500">
                Scan/Type LOA#
              </label>
              <div className="mt-2 flex gap-2">
                <input
                  value={loaInput}
                  onChange={(event) => setLoaInput(event.target.value)}
                  onBlur={(event) =>
                    setLoaInput(normalizeLoaScanInput(event.currentTarget.value))
                  }
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      if (isFetching) return;
                      handleScanSubmit(event.currentTarget.value);
                    }
                  }}
                  placeholder="Scan/Type LOA#"
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold uppercase tracking-wide focus:border-indigo-500 focus:outline-none"
                />
                <button
                  type="button"
                  onClick={() => handleScanSubmit()}
                  disabled={isFetching}
                  className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
                >
                  <FontAwesomeIcon icon={faPlus} />
                  Add
                </button>
              </div>
            </div>
            <div className="md:col-span-3">
              <label className="text-xs font-semibold uppercase text-slate-500">
                Provider
              </label>
              <input
                value={providerName}
                readOnly
                placeholder="Provider"
                className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 bg-slate-50"
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs font-semibold uppercase text-slate-500">
                Terms
              </label>
              <input
                value={paymentTerms}
                readOnly
                placeholder="Terms"
                className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 bg-slate-50"
              />
            </div>
            <div className="md:col-span-3">
              <label className="text-xs font-semibold uppercase text-slate-500">
                Due Date
              </label>
              <input
                value={dueDate}
                readOnly
                placeholder="Select date received first"
                className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 bg-slate-50"
              />
            </div>
          </div>

          <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-12">
            <div className="md:col-span-4">
              <label className="text-xs font-semibold uppercase text-slate-500">
                SOA/Ref# <span className="text-red-500">*</span>
              </label>
              <input
                value={soaRef}
                onChange={(event) => setSoaRef(event.target.value)}
                placeholder="SOA/Ref#"
                className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none"
              />
            </div>
            <div className="md:col-span-4">
              <label className="text-xs font-semibold uppercase text-slate-500">
                SOA Amount <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={soaAmount}
                onChange={(event) => setSoaAmount(event.target.value)}
                placeholder="0.00"
                className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none"
              />
            </div>
            <div className="md:col-span-4">
              <label className="text-xs font-semibold uppercase text-slate-500">
                Actual Claim Count <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="0"
                step="1"
                value={actualClaimCount}
                onChange={(event) => setActualClaimCount(event.target.value)}
                placeholder="0"
                className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none"
              />
            </div>
          </div>

          <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-12">
            <div className="md:col-span-6">
              <label className="text-xs font-semibold uppercase text-slate-500">
                Date Received <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={dateReceived}
                onChange={(event) => handleDateReceivedChange(event.target.value)}
                max={todayInputDate}
                className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none"
              />
              <button
                type="button"
                onClick={handleCopyDateReceived}
                className="mt-2 inline-flex items-center gap-1 rounded-md border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"
              >
                Copy Date
              </button>
            </div>
            <div className="md:col-span-6">
              <label className="text-xs font-semibold uppercase text-slate-500">
                Received By
              </label>
              <input
                value={receivedBy}
                readOnly
                placeholder="Received By"
                className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 bg-slate-50"
              />
            </div>
          </div>

          <div className="mt-5">
            <label className="text-xs font-semibold uppercase text-slate-500">
              Batch Remarks
            </label>
            <textarea
              rows={3}
              value={remarks}
              onChange={(event) => setRemarks(event.target.value)}
              placeholder="Batch Remarks"
              className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none"
            />
          </div>

          <div className="mt-6 rounded-xl border border-slate-200">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-4 py-3">
              <p className="text-sm font-semibold text-slate-700">
                LOA List ({filteredRows.length})
              </p>
              <div className="flex items-center gap-3 text-xs text-slate-500">
                <span>{selectCountLabel}</span>
                <button
                  type="button"
                  onClick={handleRemoveSelected}
                  disabled={selectedLoas.length === 0}
                  className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                >
                  <FontAwesomeIcon icon={faTrash} />
                  Remove Selected
                </button>
              </div>
            </div>
            {blockedRows.length > 0 && (
              <div className="border-b border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800">
                {blockedRows.length} LOA cannot be batched because Claim Status is not empty.
                (Exception: Extracted + {allowedCoverageLabel} allowed.)
                Remove them to proceed.
              </div>
            )}
            {/*
              OLD SPEC (receive-only + RTP excluded):
              {receiveOnlyRows.length > 0 && (
                <div className="border-b border-sky-200 bg-sky-50 px-4 py-3 text-xs text-sky-800">
                  {receiveOnlyRows.length} LOA marked as Receive-only. They will be recorded
                  in Date Received but excluded from batch creation.
                </div>
              )}
              {rtpRows.length > 0 && (
                <div className="border-b border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800">
                  {rtpRows.length} LOA marked as For RTP. They will be received but excluded
                  from batch creation.
                </div>
              )}
            */}
            <div className="flex flex-wrap items-center gap-3 border-b border-slate-200 bg-slate-50 px-4 py-3">
              <div className="flex-1 min-w-[240px]">
                <p className="text-xs font-semibold uppercase text-slate-500">
                  Total LOA Amount
                </p>
                <p className="text-lg font-semibold text-slate-900">
                  {formatAmount(totalLoaAmount)}
                </p>
              </div>
              <div className="flex-1 min-w-[240px]">
                <p className="text-xs font-semibold uppercase text-slate-500">
                  Total Claim Amount
                </p>
                <p className="text-lg font-semibold text-slate-900">
                  {formatAmount(totalClaimAmount)}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-4 py-3">
              <div className="text-xs text-slate-500">
                Page {page} of {totalPages}
              </div>
              <input
                value={searchTerm}
                onChange={(event) => {
                  setSearchTerm(event.target.value);
                  setPage(1);
                }}
                placeholder="Search LOA#, Coverage, Date Received"
                className="w-full max-w-xs rounded-md border border-slate-200 px-3 py-1.5 text-xs text-slate-600 focus:border-indigo-500 focus:outline-none"
              />
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                  <tr>
                    <th className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={handleToggleAll}
                      />
                    </th>
                    <th className="px-4 py-3">LOA#</th>
                    <th className="px-4 py-3">Coverage</th>
                    <th className="px-4 py-3">LOA Amount</th>
                    <th className="px-4 py-3">Claim Amount</th>
                    <th className="px-4 py-3">Claim Status</th>
                    <th className="px-4 py-3">Staled</th>
                    <th className="px-4 py-3">Date Received</th>
                    <th className="px-4 py-3">Received By</th>
                    <th className="px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedRows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={10}
                        className="px-4 py-6 text-center text-sm text-slate-500"
                      >
                        No LOA records found.
                      </td>
                    </tr>
                  ) : (
                    pagedRows.map((row) => (
                      // {/* OLD SPEC (receive-only styling):
                      // <tr
                      //   key={row.loaNo}
                      //   className={`border-t ${
                      //     isBlockedRow(row)
                      //       ? "border-red-200 bg-red-50"
                      //       : isReceiveOnlyRow(row)
                      //       ? "border-sky-200 bg-sky-50"
                      //       : row.claimStatus
                      //       ? "border-amber-200 bg-amber-50"
                      //       : "border-slate-100"
                      //   }`}
                      // >
                      // */}
                      <tr
                        key={row.loaNo}
                        className={`border-t ${
                          isBlockedRow(row)
                            ? "border-red-200 bg-red-50"
                            : row.claimStatus
                            ? "border-amber-200 bg-amber-50"
                            : "border-slate-100"
                        }`}
                      >
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={selectedLoas.includes(row.loaNo)}
                            onChange={() => handleToggleRow(row.loaNo)}
                          />
                        </td>
                        <td className="px-4 py-3 font-semibold text-slate-800">
                          {row.loaNo}
                        </td>
                        <td className="px-4 py-3 text-slate-600">
                          {row.coverageCode}
                        </td>
                        <td className="px-4 py-3 text-slate-600">
                          {formatAmount(row.loaAmount)}
                        </td>
                        <td className="px-4 py-3 text-slate-600">
                          {formatAmount(row.claimAmount)}
                        </td>
                        {/* OLD SPEC (receive-only status badge):
                        <td className="px-4 py-3">
                          {row.claimStatus ? (
                            <span
                              className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${
                                isBlockedRow(row)
                                  ? "bg-red-100 text-red-700"
                                  : isReceiveOnlyRow(row)
                                  ? "bg-sky-100 text-sky-700"
                                  : "bg-amber-100 text-amber-700"
                              }`}
                            >
                              {isAllowedExtractedCoverage(row)
                                ? `${row.claimStatus} (Allowed)`
                                : isReceiveOnlyRow(row)
                                ? `${row.claimStatus} (Receive-only)`
                                : row.claimStatus}
                            </span>
                          ) : (
                            <span className="inline-flex items-center rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-700">
                              Available
                            </span>
                          )}
                        </td>
                        */}
                        <td className="px-4 py-3">
                          {row.claimStatus ? (
                            <span
                              className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${
                                isBlockedRow(row)
                                  ? "bg-red-100 text-red-700"
                                  : "bg-amber-100 text-amber-700"
                              }`}
                            >
                              {isAllowedExtractedCoverage(row)
                                ? `${row.claimStatus} (Allowed)`
                                : row.claimStatus}
                            </span>
                          ) : (
                            <span className="inline-flex items-center rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-700">
                              Available
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-col gap-1 text-xs">
                            <span
                              className={`inline-flex items-center rounded-full px-2.5 py-1 font-semibold ${
                                row.stale
                                  ? "bg-rose-100 text-rose-700"
                                  : "bg-emerald-100 text-emerald-700"
                              }`}
                            >
                              {row.stale ? "Staled" : "No"}
                            </span>
                            {row.rtpTag && (
                              <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-1 font-semibold text-amber-700">
                                For RTP
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-slate-600">
                          {row.dateReceived}
                        </td>
                        <td className="px-4 py-3 text-slate-600">
                          {row.receivedBy}
                        </td>
                        <td className="px-4 py-3">
                          {/* OLD SPEC (RTP action buttons moved to batcher):
                          <div className="flex flex-wrap items-center gap-2">
                            <button
                              type="button"
                              onClick={() => handleOpenRtpModal(row)}
                              className="inline-flex items-center gap-1 rounded-md border border-amber-200 px-2 py-1 text-xs text-amber-700 hover:bg-amber-50"
                            >
                              {row.rtpTag ? "Edit RTP" : "For RTP"}
                            </button>
                            {row.rtpTag && (
                              <button
                                type="button"
                                onClick={() => handleCancelRtpTag(row.loaNo)}
                                className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-xs text-slate-600 hover:bg-slate-50"
                              >
                                Cancel RTP
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => handleRemoveRow(row.loaNo)}
                              className="inline-flex items-center gap-1 rounded-md border border-red-200 px-2 py-1 text-xs text-red-600 hover:bg-red-50"
                            >
                              <FontAwesomeIcon icon={faTrash} />
                              Remove
                            </button>
                          </div>
                          */}
                          <div className="flex flex-wrap items-center gap-2">
                            <button
                              type="button"
                              onClick={() => handleRemoveRow(row.loaNo)}
                              className="inline-flex items-center gap-1 rounded-md border border-red-200 px-2 py-1 text-xs text-red-600 hover:bg-red-50"
                            >
                              <FontAwesomeIcon icon={faTrash} />
                              Remove
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-end gap-2 px-4 py-3">
              <button
                type="button"
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                disabled={page === 1}
                className="rounded-md border border-slate-200 px-3 py-1 text-xs text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                type="button"
                onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                disabled={page === totalPages}
                className="rounded-md border border-slate-200 px-3 py-1 text-xs text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
            <div className="text-xs text-slate-500">
              {isFetching ? "Fetching LOA details..." : ""}
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={handleClose}
                className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50"
              >
                <FontAwesomeIcon icon={faXmark} />
                Close
              </button>
              <button
                type="button"
                onClick={handleProceed}
                disabled={blockedRows.length > 0}
                className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
              >
                <FontAwesomeIcon icon={faCheck} />
                Proceed Submit to Create Batch
              </button>
            </div>
          </div>

          {/*
            OLD SPEC (RTP modal moved to batcher):
            {isRtpModalOpen && (
              <div className="fixed inset-0 z-60 flex items-center justify-center bg-black/60 px-4 py-6">
                <div className="w-full max-w-4xl overflow-hidden rounded-2xl bg-white shadow-2xl">
                  <div className="flex items-center justify-between border-b border-slate-100 p-6">
                    <div>
                      <h3 className="text-lg font-semibold text-slate-800">For RTP</h3>
                      <p className="text-sm text-slate-500">
                        Provide RTP reason and remarks before confirming.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={handleCloseRtpModal}
                      className="text-slate-500 hover:text-slate-700"
                    >
                      <FontAwesomeIcon icon={faXmark} />
                    </button>
                  </div>
                  <div className="grid grid-cols-1 gap-6 p-6 lg:grid-cols-2">
                    <div className="space-y-2 rounded-xl bg-slate-50 p-4 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-500">LOA No</span>
                        <span className="font-semibold text-slate-700">
                          {rtpModalRow?.loaNo || "-"}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Transaction No</span>
                        <span className="font-semibold text-slate-700">
                          {rtpModalRow?.transNo || "-"}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Coverage</span>
                        <span className="font-semibold text-slate-700">
                          {rtpModalRow?.coverageCode || "-"}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Staled</span>
                        <span className="font-semibold text-slate-700">
                          {rtpModalRow?.stale ? "Yes" : "No"}
                        </span>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <label className="text-sm text-slate-500">Reason</label>
                        <select
                          value={rtpModalReason}
                          onChange={(event) => setRtpModalReason(event.target.value)}
                          className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                        >
                          <option value="">Select reason</option>
                          {rtpReasons.map((reason) => (
                            <option key={reason.rtp_code} value={reason.rtp_code}>
                              {reason.reasons}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="text-sm text-slate-500">Remarks</label>
                        <textarea
                          value={rtpModalRemarks}
                          onChange={(event) => setRtpModalRemarks(event.target.value)}
                          rows={3}
                          className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                        />
                      </div>
                      <div>
                        <label className="text-sm text-slate-500">
                          Upload Attachments (optional, max {maxRtpUploadLimit})
                        </label>
                        <input
                          type="file"
                          multiple
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={(event) => handleRtpFileChange(event.target.files)}
                          className="mt-2"
                        />
                        {rtpUploadQueue.length > 0 && (
                          <div className="mt-3 space-y-2 text-xs">
                            {rtpUploadQueue.map((item) => (
                              <div
                                key={item.file.name}
                                className="rounded-lg bg-slate-50 px-3 py-2"
                              >
                                <div className="flex items-center justify-between">
                                  <span className="truncate">{item.file.name}</span>
                                  {item.isUploading && (
                                    <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                                  )}
                                </div>
                                <div className="mt-2 h-2 rounded-full bg-slate-200">
                                  <div
                                    className="h-2 rounded-full bg-emerald-500"
                                    style={{ width: `${item.progress}%` }}
                                  />
                                </div>
                                {item.error && (
                                  <p className="mt-1 text-red-500">Error: {item.error}</p>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                        {rtpAttachments.length > 0 && (
                          <ul className="mt-3 space-y-2 text-xs">
                            {rtpAttachments.map((file) => (
                              <li
                                key={file.upload_id}
                                className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2"
                              >
                                <span className="truncate">{file.original_file_name}</span>
                                <button
                                  type="button"
                                  onClick={() => handleRemoveRtpAttachment(file)}
                                  className="text-red-600 hover:text-red-700"
                                >
                                  Remove
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-end gap-3 border-t border-slate-100 p-6">
                    <button
                      type="button"
                      onClick={handleCloseRtpModal}
                      className="inline-flex items-center gap-2 rounded-lg bg-slate-200 px-4 py-2 text-slate-800 hover:bg-slate-300"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleConfirmRtp}
                      className="inline-flex items-center gap-2 rounded-lg bg-amber-600 px-4 py-2 text-white hover:bg-amber-700"
                    >
                      Confirm RTP
                    </button>
                  </div>
                </div>
              </div>
            )}
          */}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ScanLoaDocumentQrModal;
